import { useCapabilities } from '@office-start/capabilities-service';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useEdgeworthConfig } from '@office-start/edgeworth-config-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { useFolders, useInitializeFolders, } from '@office-start/folders-service';
import { useIdentity } from '@office-start/identity-service';
import { useLocStrings } from '@office-start/loc-service';
import { logFeature } from '@office-start/telemetry-service';
import { useEffect, useMemo } from 'react';
import { documentsCardId } from '../models/Constants';
import { useEdgeworthDocumentsStore } from '../store/useEdgeworthDocumentsStore';
import { convertCardToDocument } from '../utilities/convertCardToDocument';
import { parseEdgeworthData, } from '../utilities/parseEdgeworthData';
import { useEdgeworthData } from './useEdgeworthData';
import { useEdgeworthLocalDocuments } from './useEdgeworthLocalDocuments';
import { useShouldInitializeFolders } from './useShouldInitializeFolders';
export var useSingletonEdgeworthDocuments = function (filterFileType, preloadedCacheEntries) {
    var _a;
    var locStrings = useLocStrings().data;
    var identity = useIdentity().data;
    var config = useEdgeworthConfig().data;
    // TODO: Move to edgeworth config or fetch from feature gates directly
    var capabilities = useCapabilities().data;
    var featureGates = useFeatureGates().data;
    /**
     * Feature flag indicating whether the Loop RaaS (Repository as a Service) Page fix is enabled.
     * When enabled, this disables the download, share, and open file location actions for that item.
     */
    var loopRaasPagesFixEnabled = useMemo(function () {
        var _a, _b;
        return (((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.edgeworthConfigGates) === null || _b === void 0 ? void 0 : _b.edgeworthConfigLoopRaasPagesFixEnabled) || false);
    }, [featureGates]);
    var draftEnabled = useMemo(function () {
        var _a, _b;
        return ((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.draftGates) === null || _b === void 0 ? void 0 : _b.draftEnabled) || false;
    }, [featureGates]);
    var context = useOfficeComponentContext().data;
    var _b = useEdgeworthDocumentsStore(), documents = _b.documents, documentsMap = _b.documentsMap, mergeDocumentsIntoMap = _b.mergeDocumentsIntoMap;
    var _c = useEdgeworthData(preloadedCacheEntries, filterFileType), changedDataSubject = _c.changedDataSubject, isError = _c.isError, isInitialStable = _c.isInitialStable, resync = _c.resync, msaUserHint = _c.msaUserHint;
    var localDocuments = useEdgeworthLocalDocuments();
    useEffect(function () {
        mergeDocumentsIntoMap(localDocuments);
    }, [localDocuments, mergeDocumentsIntoMap]);
    var edgeworthConfigGates = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.edgeworthConfigGates;
    var shouldInitializeFolders = useShouldInitializeFolders();
    var isFoldersBackupEnabled = !!((edgeworthConfigGates === null || edgeworthConfigGates === void 0 ? void 0 : edgeworthConfigGates.edgeworthConfigTaggingEnabled) &&
        (edgeworthConfigGates === null || edgeworthConfigGates === void 0 ? void 0 : edgeworthConfigGates.edgeworthConfigFoldersBackupEnabled));
    useInitializeFolders(isFoldersBackupEnabled && shouldInitializeFolders);
    var foldersData = useFolders();
    useEffect(function () {
        var _a;
        if (!isFoldersBackupEnabled || !(foldersData === null || foldersData === void 0 ? void 0 : foldersData.foldersClient)) {
            return;
        }
        var foldersClient = foldersData.foldersClient;
        var documentCards = Array.from(foldersClient.viewBy('fullView', {
            cardId: documentsCardId,
        })).filter(function (card) {
            var document = card;
            return (card.uniqueId !== documentsCardId &&
                (document.url || document.webUrl) &&
                !documentsMap.has(card.uniqueId));
        });
        if (documentCards.length > 0) {
            var featureProperties = {
                featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                eventName: 'PopulateDocumentFromStore',
                params: {
                    DebugInfo: documentCards.length.toString(),
                },
            };
            var missingDocuments = [];
            try {
                missingDocuments = documentCards.map(function (card) {
                    return convertCardToDocument(card);
                });
            }
            catch (e) {
                featureProperties.params['Error'] = (_a = e === null || e === void 0 ? void 0 : e.toString) === null || _a === void 0 ? void 0 : _a.call(e);
            }
            logFeature(featureProperties);
            mergeDocumentsIntoMap(missingDocuments);
        }
    }, [
        documentsMap,
        foldersData,
        isFoldersBackupEnabled,
        mergeDocumentsIntoMap,
    ]);
    var meetingObjectsMap = useMemo(function () {
        if (config && config.meetingObjectsEnabled) {
            return new Map();
        }
        return undefined;
    }, [config]);
    var documentsFilterCallback = useMemo(function () {
        var _a, _b;
        if ((capabilities && !capabilities.createStreamPlaylistEnabled) ||
            (config && !config.meetingObjectsEnabled) ||
            (featureGates &&
                !((_b = (_a = featureGates.result) === null || _a === void 0 ? void 0 : _a.edgeworthConfigGates) === null || _b === void 0 ? void 0 : _b.edgeworthConfigLinksEnabled))) {
            // filter out types if their corresponding flight isn't enabled
            return function (item) {
                var _a, _b;
                return ((capabilities === null || capabilities === void 0 ? void 0 : capabilities.createStreamPlaylistEnabled) ||
                    (item.type !== 'PlayLists' && item.type !== 'PlayList')) &&
                    ((config === null || config === void 0 ? void 0 : config.meetingObjectsEnabled) ||
                        item.type !== 'MeetingObject') &&
                    (((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.edgeworthConfigGates) === null || _b === void 0 ? void 0 : _b.edgeworthConfigLinksEnabled) ||
                        item.type !== 'Link');
            };
        }
        return undefined;
    }, [capabilities, config, featureGates]);
    var isRtl = document.dir === 'rtl';
    useEffect(function () {
        if (!identity ||
            // For consumer, make sure we have msaUserHint before parsing EW data
            (identity.isMsa && !msaUserHint) ||
            !locStrings ||
            !config ||
            !capabilities ||
            !context) {
            return;
        }
        var isMounted = true;
        var subscription = changedDataSubject.subscribe(function (changedFiles) {
            if (!isMounted) {
                return;
            }
            var documents = parseEdgeworthData({
                fileItems: changedFiles,
                untitledFileNameLocString: locStrings.edgeworthResources.untitledFileName,
                filePathTitleLocString: locStrings.edgeworthResources.filePathTitle,
                userPrincipalName: identity.userPrincipalName || '',
                meetingObjectsMap: meetingObjectsMap,
                filterCallback: documentsFilterCallback,
                outgoingLoginHintEnabled: capabilities.outgoingLoginHintEnabled,
                loginHint: context.loginHint,
                msaUserHint: msaUserHint,
                sharedActivityLocString: locStrings.edgeworthResources.sharedActivityText,
                sentAttachmentActivityLocString: locStrings.edgeworthResources.sentAttachmentActivityText,
                editActivityLocString: locStrings.edgeworthResources.editActivityText,
                isRtl: isRtl,
                oneDrivePathTitleLocString: locStrings.edgeworthItemResources.oneDrivePathTitle,
                unknownUserTextLocString: locStrings.edgeworthResources.unknownUserText,
                yourOneDrivePathTitleLocString: locStrings.edgeworthItemResources.yourOneDrivePathTitle,
                loopRaasPagesFixEnabled: loopRaasPagesFixEnabled,
                draftEnabled: draftEnabled,
            });
            mergeDocumentsIntoMap(documents);
        });
        return function () {
            isMounted = false;
            subscription.unsubscribe();
        };
    }, [
        capabilities,
        changedDataSubject,
        config,
        context,
        documentsFilterCallback,
        draftEnabled,
        identity,
        isRtl,
        locStrings,
        loopRaasPagesFixEnabled,
        meetingObjectsMap,
        mergeDocumentsIntoMap,
        msaUserHint,
    ]);
    return useMemo(function () {
        var result = {
            documents: documents,
            isError: isError,
            isInitialStable: isInitialStable,
            msaUserHint: msaUserHint,
            resync: resync,
        };
        return result;
    }, [documents, isError, isInitialStable, msaUserHint, resync]);
};
//# sourceMappingURL=useSingletonEdgeworthDocuments.js.map